<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { SemesterTab } from '@/stores/semesters'

defineProps<{ tabs: SemesterTab[]; modelValue: string | null }>()
const emit = defineEmits<{ 'update:modelValue': [code: string] }>()
const { t, locale } = useI18n()
const fullName = (tab: SemesterTab) => (locale.value === 'en' ? tab.nameEn : tab.nameCs)
</script>

<template>
    <!-- A Material 3 segmented button: the selected segment is filled and checked. -->
    <v-btn-toggle
        :model-value="modelValue"
        class="semester-switch"
        selected-class="semester-switch__segment--on"
        variant="text"
        density="comfortable"
        border
        divided
        mandatory
        @update:model-value="(code: unknown) => emit('update:modelValue', String(code))"
    >
        <v-btn
            v-for="tab in tabs"
            :key="tab.code"
            :value="tab.code"
            :data-test="`semester-tab-${tab.code}`"
            class="semester-switch__segment"
        >
            <v-icon v-if="tab.code === modelValue" icon="mdi-check" size="18" start />
            {{ t(`semester.slot.${tab.slot}`) }}
            <span v-if="tab.slot === 'current'" class="semester-switch__dot" role="img" :aria-label="t('semester.current')" />
            <span class="semester-switch__code">{{ tab.code }}</span>
            <v-tooltip activator="parent" location="bottom">
                {{ fullName(tab) }}<template v-if="tab.slot === 'current'"> · {{ t('semester.current') }} </template>
            </v-tooltip>
        </v-btn>
    </v-btn-toggle>
</template>

<style scoped>
.semester-switch {
    height: 40px;
    background: rgb(var(--v-theme-surface));
}

.semester-switch__segment {
    padding-inline: 16px;
    font-weight: 500;
    letter-spacing: 0.01em;
}

.semester-switch__segment--on {
    background: rgb(var(--v-theme-secondary-container));
    color: rgb(var(--v-theme-on-secondary-container));
}

/* The fill already marks the selection; the toggle's grey overlay would only muddy it. */
.semester-switch__segment--on :deep(.v-btn__overlay) {
    opacity: 0 !important;
}

.semester-switch__code {
    margin-inline-start: 8px;
    font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.7;
}

.semester-switch__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-inline-start: 6px;
    align-self: flex-start;
    margin-block-start: 6px;
    border-radius: 50%;
    background: rgb(var(--v-theme-success));
}

/* On a phone the three labels fit only without their codes; the tooltip still names the semester. */
@media (max-width: 599px) {
    .semester-switch__segment {
        padding-inline: 12px;
    }

    .semester-switch__code {
        display: none;
    }
}
</style>
